'use client';

import { useCallback, useMemo, useState } from 'react';

import { MetaType, ORDER } from '@repo/types';
import { usePagination } from '@repo/ui/hooks/api/use-pagination';
import { debounce } from '@repo/ui/lib/debounce';

import { HOME_TAB } from '@/@types/enums';
import { channelAvatarForBox } from '@/lib/channel-avatar-url';
import { BrandListItem } from '@/hooks/api/brand/model';
import { useListBrandsInfinite } from '@/hooks/api/brand/use-brand';
import { useListChannelsInfinite } from '@/hooks/api/channel/use-channel';
import { useAppTabs } from '@/providers/app-tabs-provider';

/**
 * Constants
 */
const ROW_HEIGHT = 32;
const DEBOUNCE_DELAY = 300;
const PAGINATION_TAKE = 50;
const STALE_TIME_BRANDS = 5 * 60 * 1000; // 5 minutes
const STALE_TIME_CHANNELS = 2 * 60 * 1000; // 2 minutes

/**
 * Shared props for both brands and channels hooks
 */
interface BaseNavigatorHookProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  brand?: BrandListItem;
  setBrand?: (brand: BrandListItem) => void;
  bypassSelectValidation?: boolean;
}

/**
 * Shared return type structure
 */
interface BaseNavigatorHookReturn {
  internSearch: string;
  setInternSearch: (internSearch: string) => void;
  handleSearch: (search: string) => void;
  handleSearchChange: (value: string) => void;
  selectBrand: (brand: BrandListItem) => void;
  meta?: MetaType;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  fetchMoreOnScroll: (
    containerRefElement: React.UIEvent<HTMLDivElement>,
  ) => void;
}

/**
 * Brands hook specific return
 */
interface UseAppNavigatorBrandsReturn extends BaseNavigatorHookReturn {
  brands: BrandListItem[];
}

/**
 * Custom hook for managing brands navigation with infinite scroll and search
 */
export const useAppNavigatorBrands = ({
  open,
  setOpen,
  brand,
  setBrand,
  bypassSelectValidation,
}: BaseNavigatorHookProps): UseAppNavigatorBrandsReturn => {
  const { setHomeTab } = useAppTabs();
  const [internSearch, setInternSearch] = useState('');

  const [{ pagination, search, handleSearch }] = usePagination({
    take: PAGINATION_TAKE,
    initialPage: 1,
    initialOrder: ORDER.ASC,
    initialOrderByColumn: 'brand',
  });

  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } =
    useListBrandsInfinite(
      {
        ...pagination,
        isActive: true,
        search: search || undefined,
      },
      {
        query: {
          enabled: open, // Only fetch when popover is open
          refetchOnWindowFocus: false,
          staleTime: STALE_TIME_BRANDS,
          getNextPageParam: (lastPage, _, lastPageParam) =>
            !lastPage.data.meta.hasNextPage
              ? undefined
              : {
                  ...lastPageParam,
                  page: lastPage.data.meta.page + 1,
                },
        },
      },
    );

  const brands = useMemo(
    () => data?.pages.flatMap((page) => page.data.data) || [],
    [data?.pages],
  );

  const fetchMoreOnScroll = useCallback(
    (containerRefElement: React.UIEvent<HTMLDivElement>) => {
      const { scrollHeight, scrollTop, clientHeight } =
        containerRefElement.currentTarget;

      if (
        scrollTop !== 0 &&
        scrollHeight - scrollTop - clientHeight < ROW_HEIGHT * 2 &&
        !isFetchingNextPage &&
        hasNextPage
      ) {
        fetchNextPage();
      }
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage],
  );

  const selectBrand = useCallback(
    (newBrand: BrandListItem) => {
      if (
        (brand?.id === newBrand?.id || !brand?.id) &&
        !bypassSelectValidation
      ) {
        setOpen(false);
        return;
      }
      setHomeTab(HOME_TAB.NEWS);
      setBrand?.(newBrand);
      setOpen(false);
    },
    [brand?.id, bypassSelectValidation, setBrand, setHomeTab, setOpen],
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((searchTerm: string) => {
        handleSearch(searchTerm);
      }, DEBOUNCE_DELAY),
    [handleSearch],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setInternSearch(value);
      debouncedSearch(value);
    },
    [debouncedSearch],
  );

  return {
    selectBrand,
    handleSearchChange,
    internSearch,
    setInternSearch,
    handleSearch,
    brands,
    meta: data?.pages[0]?.data.meta,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    fetchMoreOnScroll,
  };
};

/**
 * Channels hook specific return
 */
interface UseAppNavigatorChannelsReturn extends BaseNavigatorHookReturn {
  channels: BrandListItem[];
}

/**
 * Custom hook for managing YouTube channels navigation with infinite scroll and search
 */
export const useAppNavigatorChannels = ({
  open,
  setOpen,
  brand,
  setBrand,
  bypassSelectValidation,
}: BaseNavigatorHookProps): UseAppNavigatorChannelsReturn => {
  const [internSearch, setInternSearch] = useState('');

  const [{ pagination, search, handleSearch }] = usePagination({
    take: PAGINATION_TAKE,
    initialPage: 1,
    initialOrder: ORDER.ASC,
    initialOrderByColumn: 'brand',
  });

  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } =
    useListChannelsInfinite(
      {
        ...pagination,
        search: search || undefined,
      },
      {
        query: {
          enabled: open, // Only fetch when popover is open
          staleTime: STALE_TIME_CHANNELS,
          refetchOnWindowFocus: false,
          // PROD /channels ignores `page` (it pages ONLY via an explicit
          // `skip`) and echoes meta.page/hasNextPage as if page=1, so both
          // are unreliable. Advance by rows loaded; stop against itemCount.
          getNextPageParam: (lastPage, allPages, lastPageParam) => {
            const loaded = allPages.reduce(
              (n, p) => n + p.data.channels.length,
              0,
            );
            if (
              lastPage.data.channels.length === 0 ||
              loaded >= lastPage.data.meta.itemCount
            ) {
              return undefined;
            }
            return { ...lastPageParam, skip: loaded };
          },
        },
      },
    );

  const channels = useMemo(
    () => data?.pages.flatMap((page) => page.data.channels) || [],
    [data?.pages],
  );

  const fetchMoreOnScroll = useCallback(
    (containerRefElement: React.UIEvent<HTMLDivElement>) => {
      const { scrollHeight, scrollTop, clientHeight } =
        containerRefElement.currentTarget;

      if (
        scrollTop !== 0 &&
        scrollHeight - scrollTop - clientHeight < ROW_HEIGHT * 2 &&
        !isFetchingNextPage &&
        hasNextPage
      ) {
        fetchNextPage();
      }
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage],
  );

  const selectBrand = useCallback(
    (newBrand: BrandListItem) => {
      if (
        (brand?.id === newBrand?.id || !brand?.id) &&
        !bypassSelectValidation
      ) {
        setOpen(false);
        return;
      }
      setBrand?.(newBrand);
      setOpen(false);
    },
    [brand?.id, bypassSelectValidation, setBrand, setOpen],
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((searchTerm: string) => {
        handleSearch(searchTerm);
      }, DEBOUNCE_DELAY),
    [handleSearch],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setInternSearch(value);
      debouncedSearch(value);
    },
    [debouncedSearch],
  );

  // Transform channels to BrandListItem format with channelHandle
  const channelsTransformed = useMemo<
    (BrandListItem & { channelHandle?: string | null })[]
  >(() => {
    return channels.map(
      (channel) =>
        ({
          id: channel.id as unknown as number,
          brand: channel.name,
          // AppNavigatorLogo draws it at up to 48px; the API stores the
          // 800px original.
          logoCircleUrl:
            channelAvatarForBox(channel.chanelProfilePictureUrl, 48) ?? null,
          metrics: null,
          createdAt: channel.createdAt,
          updatedAt: channel.updatedAt,
          description: channel.description,
          isActive: true,
          channelHandle: channel.channelHandle,
        }) as BrandListItem & { channelHandle?: string | null },
    );
  }, [channels]);

  return {
    selectBrand,
    handleSearchChange,
    internSearch,
    setInternSearch,
    handleSearch,
    channels: channelsTransformed,
    meta: data?.pages[0]?.data.meta,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    fetchMoreOnScroll,
  };
};
